import { useGetCurrentPatientQuery as useGetCurrentPatientQueryBase } from '../../../generated/graphql';

import { useAuth0 as useAuth0Base } from '@auth0/auth0-react';
import { useAtom as useAtomBase } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams as useParamsBase } from 'react-router-dom';

import { RuntimeEnvironmentOptions, getEnvFromHostname } from '@growtherapy/analytics';

import { logger } from '../../../datadog';
import { visitorNameAtom } from '../../state';

const STORED_VISITOR_NAMES_KEY = 'VISITOR_NAME_5CFEB82B-BE4D-4659-B6AE-5096524DE190';

// Mapping from patientShortID to visitor name
type StoredVisitorNames = Record<string, string>;

function parseStoredVisitorNames(rawValue: string): StoredVisitorNames {
  try {
    // Try to parse the value as JSON
    const parsedValue = JSON.parse(rawValue) as Record<string, unknown>;
    // If the parsed value is an object, it's likely a valid format.
    if (typeof parsedValue === 'object' && parsedValue !== null && !Array.isArray(parsedValue)) {
      return parsedValue as StoredVisitorNames;
    }
  } catch (_) {
    // This just means whatever value that was set is invalid, so we ignore it.
    logger.warn('Found invalid value stored for STORED_VISITOR_NAMES_KEY:', {
      rawValue,
    });
  }
  return {};
}

function shouldPersist() {
  const env = getEnvFromHostname({ hostname: window.location.hostname });
  const urlParams = new URLSearchParams(window.location.search);
  return (
    // Always enabled in local
    env === RuntimeEnvironmentOptions.local ||
    // Enabled in non-production if URL param is set
    (env !== RuntimeEnvironmentOptions.production && urlParams.has('prefill', 'true'))
  );
}

function getStoredVisitorName(patientShortID: string) {
  if (!shouldPersist()) return null;
  const names = localStorage.getItem(STORED_VISITOR_NAMES_KEY);
  if (!names) return null;
  const parsedNames = parseStoredVisitorNames(names);
  return parsedNames[patientShortID] ?? null;
}

function setStoredVisitorName(patientShortID: string, name: string) {
  if (!shouldPersist()) return;
  const existingNames = localStorage.getItem(STORED_VISITOR_NAMES_KEY);
  const newNames: StoredVisitorNames = {
    ...(existingNames ? parseStoredVisitorNames(existingNames) : {}),
    [patientShortID]: name,
  };
  localStorage.setItem(STORED_VISITOR_NAMES_KEY, JSON.stringify(newNames));
}

type UseVisitorNameDependencies = {
  useAtom: typeof useAtomBase;
  useAuth0: typeof useAuth0Base;
  useGetCurrentPatientQuery: typeof useGetCurrentPatientQueryBase;
  useParams: typeof useParamsBase;
};

/**
 * Custom hook for managing the visitor name in the intro room.
 *
 * This hook handles syncing the visitor name between the intro room's text
 * input field, Jotai state, external network operations that include the
 * visitor's account data in the response, and local storage.
 *
 * For authenticated users as well as scenarios where we have access to the
 * user's account data (e.g. PII verification), we can derive the visitor name
 * from what is on file for their account. In the absence of either of those,
 * the user is required to enter a name before they can enter the waiting room.
 *
 * Either way, the user's last input, if any, will take priority over other data
 * sources; those other data sources just serve as the default value.
 *
 * The local storage syncing is only used in local and staging environments for
 * testing convenience and SHOULD NOT be turned on in production, since this is
 * PII. Tracks unique visitor name per patientShortID.
 *
 * The callbacks do not perform validation on the name supplied to them. A
 * consumer should use the `validateVisitorName` function to validate the name
 * prior to setting it.
 *
 * @returns An object containing the resolved value for the visitor name and
 * callbacks for setting the visitor name from different data sources.
 * - visitorName: The final, resolved value for the visitor name.
 * - visitorNameInput: The input field value for the visitor name input field,
 *   based on user input.
 * - visitorNameIsRequired: Whether or not the visitor is required to enter a
 *   name.
 * - setVisitorName: Callback for setting the visitor name from direct user
 *   input.
 * - setAccountDataVisitorName: Callback for setting the visitor name from the
 *   user's account data in response to external network operations
 */
export function useVisitorName(
  { useParams, useAtom, useAuth0, useGetCurrentPatientQuery }: UseVisitorNameDependencies = {
    useParams: useParamsBase,
    useAtom: useAtomBase,
    useAuth0: useAuth0Base,
    useGetCurrentPatientQuery: useGetCurrentPatientQueryBase,
  },
): {
  visitorName: string;
  visitorNameInput: string;
  visitorNameIsRequired: boolean;
  setVisitorName: (name: string) => void;
  setAccountDataVisitorName: (name: string) => void;
} {
  const { patientShortId } = useParams();
  // The final, resolved visitor name that will be used when the visitor enters
  // the waiting room.
  const [visitorName, setVisitorNameAtom] = useAtom(visitorNameAtom);
  // The input field value for the visitor name input field. This may be empty
  // even when visitorName is set, if the user has not yet entered a value and
  // we resolved the name from other sources.
  const [visitorNameInput, setVisitorNameInput] = useState('');

  // Internal-only state for the visitor name derived from their external
  // account data.
  const [internalAccountDataVisitorName, setInternalAccountDataVisitorName] = useState<string | null>(null);

  // Fetch the stored visitor name from local storage if available and set it as
  // the initial value for the input visitor name.
  useEffect(
    function fetchInitialStoredVisitorName() {
      if (!patientShortId) return;
      const storedName = getStoredVisitorName(patientShortId);
      if (storedName && storedName.trim().length > 0) {
        setVisitorNameInput(storedName);
      }
    },
    [patientShortId],
  );

  // For authenticated users, we can fetch the patient's preferred name from
  // their client user using the current patient query.
  const { isAuthenticated } = useAuth0();
  const { data: patientData } = useGetCurrentPatientQuery({
    variables: { patientShortId: patientShortId! },
    skip: !isAuthenticated || !patientShortId,
  });

  /**
   * Callback when the *user* inputs a new preferred name. This input should take
   * priority over data-derived names and will be written to local storage to be
   * remembered for future visits in local/staging environments.
   */
  const setVisitorName = useCallback(
    (name: string) => {
      if (patientShortId) {
        setVisitorNameInput(name);
        setStoredVisitorName(patientShortId, name);
      }
    },
    [patientShortId],
  );

  useEffect(
    function resolveVisitorName() {
      // Priority:
      // 1. Input (or previously stored) visitor name
      // 2. Authenticated user's preferred name
      // 3. Name derived from other external account data
      if (visitorNameInput) {
        setVisitorNameAtom(visitorNameInput);
      } else if (patientData?.patientInfo?.preferredName) {
        setVisitorNameAtom(patientData.patientInfo.preferredName);
      } else if (internalAccountDataVisitorName) {
        setVisitorNameAtom(internalAccountDataVisitorName);
      }
    },
    [internalAccountDataVisitorName, patientData?.patientInfo?.preferredName, setVisitorNameAtom, visitorNameInput],
  );

  const visitorNameIsRequired = useMemo(() => {
    return !patientData?.patientInfo?.preferredName && !internalAccountDataVisitorName;
  }, [patientData?.patientInfo?.preferredName, internalAccountDataVisitorName]);

  return {
    visitorName,
    visitorNameInput,
    visitorNameIsRequired,
    setVisitorName,
    setAccountDataVisitorName: setInternalAccountDataVisitorName,
  };
}
